import { Components, CSSInterpolation, Theme } from '@mui/material'

declare module '@mui/material/Avatar' {
  interface AvatarOwnProps {
    // Avatar doesn't have a color prop in MUI, so we add one
    color?: 'default' | 'brand-muted' | 'gold' | 'mint' | 'plum' | 'lime'
    // The avatar's dimensions; the font-size scales with it
    size?: 16 | 20 | 24 | 32 | 56
    ['data-is-surplus']?: boolean
  }

  // oxlint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AvatarPropsVariantOverrides {
    // TODO - use when no more usage of rounded variant
    // rounded: false
  }
}

export const MuiAvatar: Components<Omit<Theme, 'components'>>['MuiAvatar'] = {
  defaultProps: {
    variant: 'circular',
    color: 'default',
    size: 24,
    slotProps: {
      root: ({ color }) => ({
        'data-color': color,
        // Don't leak our custom props to the DOM as invalid attributes
        color: undefined,
        size: undefined,
      }),
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderWidth: theme.strokeWidth.default,
      borderStyle: 'solid',
    }),
  },
  variants: [
    // Size — width, height and font-size scale together
    {
      props: { size: 16 },
      style: ({ theme }) => ({
        width: 16,
        height: 16,
        ...theme.typography['extraSmall-semiBold'],
        fontSize: 6,
      }),
    },
    {
      props: { size: 20 },
      style: ({ theme }) => ({
        width: 20,
        height: 20,
        ...theme.typography['extraSmall-semiBold'],
        fontSize: 8,
      }),
    },
    {
      props: { size: 24 },
      style: ({ theme }) => ({
        width: 24,
        height: 24,
        ...theme.typography['extraSmall-semiBold'],
      }),
    },
    {
      props: { size: 32 },
      style: ({ theme }) => ({
        width: 32,
        height: 32,
        ...theme.typography['small-bold'],
      }),
    },
    {
      props: { size: 56 },
      style: ({ theme }) => ({
        width: 56,
        height: 56,
        ...theme.typography['display-h3'],
      }),
    },
    // Shape
    // Convention: circular for human users, rounded/square for non-human entities (suppliers, products, integrations, etc).
    {
      // Make rounded and square both show as the same thing for now, until we remove the rounded variant from usage
      props: (props) => props.variant === 'square' || props.variant === 'rounded',
      style: ({ theme }) => ({
        borderRadius: theme.shape['radius-sm'],
      }),
    },
    // Colour
    {
      props: { color: 'default' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.bg.brand.default,
        color: theme.palette.text.brand.onBrand,
      }),
    },
    {
      props: { color: 'brand-muted' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.bg.brand.muted,
        color: theme.palette.text.brand.onBrandMuted,
      }),
    },
    {
      props: { color: 'gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.gold.muted,
        borderColor: theme.palette.border.utility.gold.muted,
        color: theme.palette.text.utility.gold.default,
      }),
    },
    {
      props: { color: 'mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.mint.muted,
        borderColor: theme.palette.border.utility.mint.muted,
        color: theme.palette.text.utility.mint.default,
      }),
    },
    {
      props: { color: 'plum' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.plum.muted,
        borderColor: theme.palette.border.utility.plum.muted,
        color: theme.palette.text.utility.plum.default,
      }),
    },
    {
      props: { color: 'lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.lime.muted,
        borderColor: theme.palette.border.utility.lime.muted,
        color: theme.palette.text.utility.lime.default,
      }),
    },
  ],
}
export const MuiAvatarGroup: Components<Omit<Theme, 'components'>>['MuiAvatarGroup'] = {
  defaultProps: {
    spacing: 0,
    slotProps: {
      surplus: {
        'data-is-surplus': true,
      },
    },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      gap: theme.spacing(1),
      alignItems: 'center',
    }),
    avatar: ({ theme }) => ({
      // MUI's AvatarGroup forces a 2px border for overlapping stacks; we don't overlap,
      // so strip it and restore each avatar's own per-colour border via data-color.
      border: 'none',
      ...(['gold', 'mint', 'plum', 'lime'] as const).reduce<Record<string, CSSInterpolation>>((acc, color) => {
        acc[`&[data-color="${color}"]`] = {
          borderStyle: 'solid',
          borderWidth: theme.strokeWidth.default,
          borderColor: theme.palette.border.utility[color].muted,
        }
        return acc
      }, {}),
      '&[data-is-surplus="true"]': {
        ...theme.typography['small-regular'],
        backgroundColor: theme.palette.bg.invisible,
        color: theme.palette.text.muted,
      },
    }),
  },
}
